import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// ============================================================
// ECOSYSTEM NAVIGATION ICONS - Beautiful mystical style
// Based on the BlissAbode ecosystem navigation design
// ============================================================

// Belonging Sessions - Layered scrolls/pages icon
const BelongingIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
);

// ElchmE - Infinity/lemniscate icon
const ElchmeIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.74-8z"/>
  </svg>
);

// M.ai.ry - Enlightened lotus/wisdom icon
const MairyIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C8 2 4 6 4 10c0 2 1 4 2 5.5C7 17 8 19 8 21h8c0-2 1-4 2-5.5 1-1.5 2-3.5 2-5.5 0-4-4-8-8-8z"/>
    <path d="M12 2v4"/>
    <path d="M4.93 10h2.14"/>
    <path d="M16.93 10h2.14"/>
    <path d="M7.76 5.76l1.52 1.52"/>
    <path d="M14.72 7.28l1.52-1.52"/>
    <circle cx="12" cy="12" r="2"/>
  </svg>
);

// Defiance Springs - Sacred flame icon
const DefianceIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z"/>
  </svg>
);

// E-book - Open book with wisdom light
const EbookIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    <path d="M12 6v7"/>
    <path d="M9 9l3-3 3 3"/>
  </svg>
);

// Feelings/Compass - Heart compass icon
const FeelingsIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 2v4"/>
    <path d="M12 18v4"/>
    <path d="M2 12h4"/>
    <path d="M18 12h4"/>
    <path d="M12 8l2 4-2 4-2-4 2-4z" fill="currentColor" opacity="0.3"/>
    <circle cx="12" cy="12" r="2" fill="currentColor"/>
  </svg>
);

// Login - Sacred spiral portal
const LoginIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
    <polyline points="10 17 15 12 10 7"/>
    <line x1="15" y1="12" x2="3" y2="12"/>
  </svg>
);

// BlissAbode Home - Sacred home/sanctuary icon
const BlissAbodeIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <path d="M9 22V12h6v10"/>
    <circle cx="12" cy="8" r="1.5" fill="currentColor" opacity="0.5"/>
  </svg>
);

// Sketch Hub - Creative pencil/sketch icon
const SketchHubIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19l7-7 3 3-7 7-3-3z"/>
    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
    <path d="M2 2l7.586 7.586"/>
    <circle cx="11" cy="11" r="2"/>
  </svg>
);

// Tome Machine - Story/Book creation icon
const TomeMachineNavIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    <path d="M8 7h8"/>
    <path d="M8 11h6"/>
    <path d="M8 15h4"/>
    <circle cx="16" cy="14" r="3" fill="currentColor" opacity="0.2"/>
    <path d="M16 11v3l2 1"/>
  </svg>
);

// Ecosystem links configuration with new icons and colors
const ecosystemLinks = [
  {
    name: 'Belonging',
    shortName: 'Belonging\nSessions',
    href: 'https://belongingsessions.com',
    icon: BelongingIcon,
    bgGradient: 'from-amber-500/20 to-amber-600/30',
    hoverGradient: 'group-hover:from-amber-500/30 group-hover:to-amber-600/40',
    borderColor: 'border-amber-500/30',
    iconColor: 'text-amber-400 group-hover:text-amber-300',
    description: 'Community Hub'
  },
  {
    name: 'E-book',
    shortName: 'E-book',
    href: 'https://belongingsessions.com/belonging-ebook',
    icon: EbookIcon,
    bgGradient: 'from-rose-500/20 to-rose-600/30',
    hoverGradient: 'group-hover:from-rose-500/30 group-hover:to-rose-600/40',
    borderColor: 'border-rose-500/30',
    iconColor: 'text-rose-400 group-hover:text-rose-300',
    description: 'Sacred Texts'
  },
  {
    name: 'ElchmE',
    shortName: 'ElchmE',
    href: 'https://elchme.com',
    icon: ElchmeIcon,
    bgGradient: 'from-purple-500/20 to-purple-600/30',
    hoverGradient: 'group-hover:from-purple-500/30 group-hover:to-purple-600/40',
    borderColor: 'border-purple-500/30',
    iconColor: 'text-purple-400 group-hover:text-purple-300',
    description: 'Research'
  },
  {
    name: 'Sketch',
    shortName: 'Sketch Hub',
    href: 'https://elchme.com/sketch',
    icon: SketchHubIcon,
    bgGradient: 'from-pink-500/20 to-fuchsia-600/30',
    hoverGradient: 'group-hover:from-pink-500/30 group-hover:to-fuchsia-600/40',
    borderColor: 'border-pink-500/30',
    iconColor: 'text-pink-400 group-hover:text-pink-300',
    description: 'Creative Studio'
  },
  {
    name: 'Tome',
    shortName: 'Tome\nMachine',
    href: '/tome',
    icon: TomeMachineNavIcon,
    bgGradient: 'from-violet-500/20 to-fuchsia-600/30',
    hoverGradient: 'group-hover:from-violet-500/30 group-hover:to-fuchsia-600/40',
    borderColor: 'border-violet-500/30',
    iconColor: 'text-violet-400 group-hover:text-violet-300',
    description: 'Story Studio',
    isInternal: true
  },
  {
    name: 'M.ai.ry',
    shortName: 'm.ai.ry',
    href: 'https://mairy.io',
    icon: MairyIcon,
    bgGradient: 'from-cyan-500/20 to-purple-500/30',
    hoverGradient: 'group-hover:from-cyan-500/30 group-hover:to-purple-500/40',
    borderColor: 'border-cyan-500/30',
    iconColor: 'text-cyan-400 group-hover:text-cyan-300',
    description: 'AI Wisdom'
  },
  {
    name: 'Feelings',
    shortName: 'Feelings',
    href: 'https://belongingsessions.com/feelings-needs',
    icon: FeelingsIcon,
    bgGradient: 'from-emerald-500/20 to-teal-600/30',
    hoverGradient: 'group-hover:from-emerald-500/30 group-hover:to-teal-600/40',
    borderColor: 'border-emerald-500/30',
    iconColor: 'text-emerald-400 group-hover:text-emerald-300',
    description: 'Compass'
  },
  {
    name: 'Defiance',
    shortName: 'Defiance',
    href: 'https://defiancesprings.com',
    icon: DefianceIcon,
    bgGradient: 'from-orange-500/20 to-red-600/30',
    hoverGradient: 'group-hover:from-orange-500/30 group-hover:to-red-600/40',
    borderColor: 'border-orange-500/30',
    iconColor: 'text-orange-400 group-hover:text-orange-300',
    description: 'Springs'
  },
  {
    name: 'Login',
    shortName: 'Login',
    href: 'https://belongingsessions.com/login',
    icon: LoginIcon,
    bgGradient: 'from-blue-500/20 to-indigo-600/30',
    hoverGradient: 'group-hover:from-blue-500/30 group-hover:to-indigo-600/40',
    borderColor: 'border-blue-500/30',
    iconColor: 'text-blue-400 group-hover:text-blue-300',
    description: 'Sign In'
  }
];

export const EcosystemNav = ({ currentSite = null }) => {
  // Filter out current site from navigation
  const links = currentSite 
    ? ecosystemLinks.filter(link => !link.href.includes(currentSite))
    : ecosystemLinks;

  return (
    <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-t border-slate-700/50">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <h3 className="text-center text-white/50 text-[9px] font-light tracking-[0.15em] mb-3 uppercase">
          Explore Our Ecosystem
        </h3>
        <div className="flex items-center justify-center gap-3 md:gap-4 flex-wrap">
          {links.map((link) => {
            const IconComponent = link.icon;
            const linkContent = (
              <>
                <div className={`
                  w-6 h-6 md:w-7 md:h-7 
                  rounded-full 
                  bg-gradient-to-br ${link.bgGradient} ${link.hoverGradient}
                  flex items-center justify-center 
                  transition-all duration-200
                  border ${link.borderColor}
                  shadow-sm shadow-black/20
                  group-hover:shadow-md group-hover:shadow-black/30
                `}>
                  <IconComponent className={`w-3 h-3 md:w-3.5 md:h-3.5 ${link.iconColor} transition-colors duration-200`} />
                </div>
                <span className="text-[7px] md:text-[8px] font-medium text-slate-500 group-hover:text-white transition-colors duration-200 text-center whitespace-pre-line leading-tight">
                  {link.name}
                </span>
              </>
            );

            // Use Link for internal routes, anchor for external
            if (link.isInternal) {
              return (
                <motion.div
                  key={link.name}
                  whileHover={{ scale: 1.1, y: -1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to={link.href}
                    title={`${link.name} - ${link.description}`}
                    className="group flex flex-col items-center gap-1 transition-all"
                  >
                    {linkContent}
                  </Link>
                </motion.div>
              );
            }

            return (
              <motion.a
                key={link.name}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                title={`${link.name} - ${link.description}`}
                className="group flex flex-col items-center gap-1 transition-all"
                whileHover={{ scale: 1.1, y: -1 }}
                whileTap={{ scale: 0.95 }}
              >
                {linkContent}
              </motion.a>
            );
          })}
        </div>
        
        {/* Part of Belonging Sessions & Copyright */}
        <div className="mt-4 pt-3 border-t border-slate-700/30 text-center">
          <p className="text-[9px] text-slate-500 mb-1">
            Part of the{' '}
            <a 
              href="https://belongingsessions.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-amber-500/70 hover:text-amber-400 transition-colors"
            >
              Belonging Sessions
            </a>
            {' '}Family
          </p>
          <p className="text-[8px] text-slate-600">
            © 2026 Belonging Sessions. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

// Alternative compact horizontal layout for headers
export const EcosystemNavCompact = ({ currentSite = null }) => {
  const links = currentSite 
    ? ecosystemLinks.filter(link => !link.href.includes(currentSite)).slice(0, 5)
    : ecosystemLinks.slice(0, 5);

  return (
    <div className="flex items-center gap-2">
      {links.map((link) => {
        const IconComponent = link.icon;
        return (
          <a
            key={link.name}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            title={link.name}
            className={`
              group w-5 h-5 rounded-full 
              bg-gradient-to-br ${link.bgGradient}
              flex items-center justify-center 
              transition-all duration-200
              border ${link.borderColor}
              hover:scale-110
            `}
          >
            <IconComponent className={`w-2.5 h-2.5 ${link.iconColor} transition-colors`} />
          </a>
        );
      })}
    </div>
  );
};

// Mobile-optimized fixed bottom navigation
export const EcosystemNavMobile = ({ currentSite = null }) => {
  const links = currentSite 
    ? ecosystemLinks.filter(link => !link.href.includes(currentSite)).slice(0, 5)
    : ecosystemLinks.slice(0, 5);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-lg border-t border-slate-700/50 safe-area-pb md:hidden">
      <div className="flex items-center justify-around px-2 py-2">
        {links.map((link) => {
          const IconComponent = link.icon;
          return (
            <a
              key={link.name}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-0.5 px-1 py-0.5 group"
            >
              <div className={`
                w-6 h-6 rounded-full 
                bg-gradient-to-br ${link.bgGradient}
                flex items-center justify-center 
                border ${link.borderColor}
                shadow-sm
              `}>
                <IconComponent className={`w-3 h-3 ${link.iconColor}`} />
              </div>
              <span className="text-[7px] font-medium text-slate-500 group-hover:text-slate-300 transition-colors">
                {link.name}
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

// Export icons for use elsewhere
export {
  BelongingIcon,
  ElchmeIcon,
  SketchHubIcon,
  TomeMachineNavIcon,
  MairyIcon,
  DefianceIcon,
  EbookIcon,
  FeelingsIcon,
  LoginIcon,
  BlissAbodeIcon
};

export default EcosystemNav;
